import React from 'react';
import * as Sentry from '@sentry/react';

interface SentryErrorBoundaryProps {
  /** Fallback UI to show when an error is caught */
  fallback?: React.ReactElement | Sentry.FallbackRender;
  /** Called when an error is caught (in addition to reporting to Sentry) */
  onError?: (error: Error, componentStack: string) => void;
  /** Show Sentry's user report dialog when an error is caught */
  showReportDialog?: boolean;
  children: React.ReactNode;
}

/**
 * Error boundary that reports caught React errors to Sentry/GlitchTip.
 *
 * Wraps Sentry's ErrorBoundary with a sensible default fallback.
 *
 * ```tsx
 * <SentryErrorBoundary fallback={<ErrorPage />}>
 *   <App />
 * </SentryErrorBoundary>
 * ```
 */
export function SentryErrorBoundary({
  fallback,
  onError,
  showReportDialog = true,
  children,
}: SentryErrorBoundaryProps) {
  const defaultFallback: React.ReactElement = (
    <div
      style={{
        padding: '2rem',
        textAlign: 'center',
        fontFamily: 'sans-serif',
      }}
    >
      <h2>Something went wrong</h2>
      <p>An unexpected error occurred. Our team has been notified.</p>
      <button onClick={() => window.location.reload()}>Reload Page</button>
    </div>
  );

  return (
    <Sentry.ErrorBoundary
      fallback={fallback || defaultFallback}
      onError={onError}
      showDialog={showReportDialog}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
